/**
 * Consent plus signature capture.
 *
 * The consent language sits directly above the signature, not buried in a
 * footer, and nothing can be submitted until it is actively checked and a
 * signature actually exists.
 */
import type { SignRequest } from '../api';
import { createSignaturePad } from '../signature-pad';
import type { SignaturePad } from '../signature-pad';
import { el } from '../ui';

export type SignatureMode = 'draw' | 'type';

/**
 * Whether the panel can be submitted, and what is still missing when it cannot.
 *
 * The caller owns the Submit button, so it needs more than a yes/no: it has to
 * be able to tell the signer which of the two steps is outstanding.
 */
export interface SignatureStatus {
  /** True when consent is given and a signature exists. */
  ready: boolean;
  needsConsent: boolean;
  needsSignature: boolean;
  /** Which tab the signature would come from, so callers can name the action. */
  mode: SignatureMode;
}

export interface SignaturePanel {
  node: HTMLElement;
  /** The current readiness, for callers that need it outside a change event. */
  status(): SignatureStatus;
  /** The request body, or null when the panel is not ready. */
  value(): SignRequest | null;
  destroy(): void;
}

export interface SignaturePanelOptions {
  /** Prefilled into the Type tab, so most signers only have to confirm it. */
  suggestedName: string;
  onValidityChange(status: SignatureStatus): void;
}

const CONSENT_TEXT =
  'I agree to sign this document electronically and I consent to receive this record, ' +
  'and notices related to it, in electronic form. I understand my electronic signature ' +
  'is legally binding, exactly as a signature made on paper would be. I may request a ' +
  'paper copy of this record at no charge, and I may withdraw my consent to electronic ' +
  'records at any time, by contacting the sender.';

export function createSignaturePanel(options: SignaturePanelOptions): SignaturePanel {
  const { suggestedName, onValidityChange } = options;

  let mode: SignatureMode = 'draw';
  let lastStatus: SignatureStatus | null = null;

  /* ---- consent --------------------------------------------------------- */

  const consent = el('input', {
    class: 'checkbox',
    attrs: { type: 'checkbox', id: 'consent' },
  });

  const consentBlock = el('div', {
    class: 'consent',
    children: [
      el('h3', { class: 'consent-title', text: 'Consent to sign electronically' }),
      el('p', { class: 'consent-body', text: CONSENT_TEXT }),
      el('div', {
        class: 'consent-check',
        children: [
          consent,
          el('label', {
            class: 'consent-label',
            text: 'I have read and agree to the statement above.',
            attrs: { for: 'consent' },
          }),
        ],
      }),
    ],
  });

  /* ---- draw tab -------------------------------------------------------- */

  const canvas = el('canvas', {
    class: 'sig-canvas',
    attrs: { 'aria-label': 'Signature drawing area. Draw your signature with your finger or mouse.', role: 'img' },
  });

  const clearButton = el('button', {
    class: 'button button-quiet button-small',
    text: 'Clear',
    attrs: { type: 'button' },
  });

  const drawPanel = el('div', {
    class: 'tab-panel',
    attrs: { id: 'panel-draw', role: 'tabpanel', 'aria-labelledby': 'tab-draw' },
    children: [
      el('p', { class: 'hint', text: 'Draw your signature in the box below.' }),
      el('div', { class: 'sig-frame', children: [canvas] }),
      el('div', { class: 'sig-tools', children: [clearButton] }),
    ],
  });

  /* ---- type tab -------------------------------------------------------- */

  const typedInput = el('input', {
    class: 'input',
    attrs: {
      type: 'text',
      id: 'typed-name',
      autocomplete: 'name',
      placeholder: 'Your full legal name',
      maxlength: '120',
    },
  });
  typedInput.value = suggestedName;

  const preview = el('div', {
    class: 'script-preview',
    attrs: { 'aria-hidden': 'true' },
    text: suggestedName,
  });

  const typePanel = el('div', {
    class: 'tab-panel',
    attrs: { id: 'panel-type', role: 'tabpanel', 'aria-labelledby': 'tab-type', hidden: 'hidden' },
    children: [
      el('div', {
        class: 'field',
        children: [
          el('label', { class: 'label', text: 'Type your full legal name', attrs: { for: 'typed-name' } }),
          typedInput,
        ],
      }),
      el('p', { class: 'hint', text: 'This is how your signature will appear.' }),
      preview,
    ],
  });

  /* ---- tabs ------------------------------------------------------------ */

  const drawTab = el('button', {
    class: 'tab tab-active',
    text: 'Draw',
    attrs: { type: 'button', id: 'tab-draw', role: 'tab', 'aria-selected': 'true', 'aria-controls': 'panel-draw' },
  });
  const typeTab = el('button', {
    class: 'tab',
    text: 'Type',
    attrs: { type: 'button', id: 'tab-type', role: 'tab', 'aria-selected': 'false', 'aria-controls': 'panel-type', tabindex: '-1' },
  });

  const tabs = el('div', {
    class: 'tabs',
    attrs: { role: 'tablist', 'aria-label': 'Signature style' },
    children: [drawTab, typeTab],
  });

  const pad: SignaturePad = createSignaturePad({ canvas, onChange: () => reportValidity() });

  function typedValue(): string {
    return typedInput.value.trim();
  }

  function hasSignature(): boolean {
    return mode === 'draw' ? !pad.isEmpty() : typedValue().length > 0;
  }

  function status(): SignatureStatus {
    const needsConsent = !consent.checked;
    const needsSignature = !hasSignature();
    return { ready: !needsConsent && !needsSignature, needsConsent, needsSignature, mode };
  }

  function sameStatus(a: SignatureStatus, b: SignatureStatus): boolean {
    return (
      a.ready === b.ready &&
      a.needsConsent === b.needsConsent &&
      a.needsSignature === b.needsSignature &&
      a.mode === b.mode
    );
  }

  /**
   * Report every change, not just the ones that flip readiness: ticking consent
   * while the signature is still missing has to move the on-screen hint on.
   */
  function reportValidity(): void {
    const next = status();
    if (lastStatus && sameStatus(lastStatus, next)) return;
    lastStatus = next;
    onValidityChange(next);
  }

  function selectMode(next: SignatureMode): void {
    mode = next;
    const drawing = next === 'draw';
    drawTab.classList.toggle('tab-active', drawing);
    typeTab.classList.toggle('tab-active', !drawing);
    drawTab.setAttribute('aria-selected', String(drawing));
    typeTab.setAttribute('aria-selected', String(!drawing));
    drawTab.setAttribute('tabindex', drawing ? '0' : '-1');
    typeTab.setAttribute('tabindex', drawing ? '-1' : '0');
    drawPanel.hidden = !drawing;
    typePanel.hidden = drawing;
    reportValidity();
  }

  const onDrawTab = (): void => selectMode('draw');
  const onTypeTab = (): void => selectMode('type');
  const onClear = (): void => {
    pad.clear();
    reportValidity();
  };
  const onTyped = (): void => {
    preview.textContent = typedInput.value;
    reportValidity();
  };
  const onConsent = (): void => reportValidity();

  const onTabKey = (event: KeyboardEvent): void => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const next: SignatureMode = mode === 'draw' ? 'type' : 'draw';
    selectMode(next);
    (next === 'draw' ? drawTab : typeTab).focus();
  };

  drawTab.addEventListener('click', onDrawTab);
  typeTab.addEventListener('click', onTypeTab);
  tabs.addEventListener('keydown', onTabKey);
  clearButton.addEventListener('click', onClear);
  typedInput.addEventListener('input', onTyped);
  consent.addEventListener('change', onConsent);

  const node = el('div', {
    class: 'sign-panel',
    children: [
      consentBlock,
      el('div', {
        class: 'sig-block',
        children: [
          el('h3', { class: 'sig-title', text: 'Your signature' }),
          tabs,
          drawPanel,
          typePanel,
        ],
      }),
    ],
  });

  return {
    node,

    status,

    value(): SignRequest | null {
      if (!consent.checked) return null;
      if (mode === 'draw') {
        const dataUrl = pad.toDataURL();
        if (!dataUrl) return null;
        return { consent: true, signatureDataUrl: dataUrl };
      }
      const name = typedValue();
      if (name.length === 0) return null;
      return { consent: true, typedName: name };
    },

    destroy(): void {
      drawTab.removeEventListener('click', onDrawTab);
      typeTab.removeEventListener('click', onTypeTab);
      tabs.removeEventListener('keydown', onTabKey);
      clearButton.removeEventListener('click', onClear);
      typedInput.removeEventListener('input', onTyped);
      consent.removeEventListener('change', onConsent);
      pad.destroy();
    },
  };
}
